/**
 * Image Proxy Helper (Phase 27: FR-127)
 *
 * Generates proxied image URLs that route through the Bridge backend
 * for caching and transformation.
 */

/** Available transformation presets */
export type ImagePreset = 'thumb' | 'preview' | 'medium' | 'large' | 'original';

/**
 * Generate a proxied image URL for a partner image.
 *
 * Routes the request through the Bridge backend image proxy,
 * which handles caching, transformation, and format conversion.
 *
 * @param partnerUrl - The original partner image URL (e.g., Guesty/Cloudinary URL)
 * @param preset - The transformation preset to apply
 * @returns Proxied URL pointing to /api/v1/image-proxy
 *
 * @example
 * // Get a preview-sized image (400px width, WebP format)
 * const url = getProxiedImageUrl(image.url, 'preview');
 * // Returns: /api/v1/image-proxy?url=<base64>&preset=preview
 */
export function getProxiedImageUrl(
  partnerUrl: string | undefined | null,
  preset: ImagePreset
): string {
  // Handle undefined/null URLs gracefully
  if (!partnerUrl) {
    // Return a placeholder image URL
    return '/placeholder-image.svg';
  }

  // Base64-encode the URL's UTF-8 bytes for safe transmission. btoa() alone
  // only accepts Latin1, so a partner URL containing non-Latin1 characters
  // (e.g. Cyrillic paths from the Bulgaria file-import partner) would throw
  // InvalidCharacterError and crash the whole list render. The backend decodes
  // with Buffer.from(val, 'base64').toString('utf-8'), so we feed btoa the
  // UTF-8 byte sequence. For pure-ASCII URLs this is byte-identical to the
  // previous btoa(partnerUrl), so existing partners are unaffected.
  const utf8Bytes = new TextEncoder().encode(partnerUrl);
  let binary = '';
  for (const byte of utf8Bytes) {
    binary += String.fromCharCode(byte);
  }
  const encodedUrl = btoa(binary);

  // Construct the proxy URL
  return `/api/v1/image-proxy?url=${encodeURIComponent(encodedUrl)}&preset=${preset}`;
}

/**
 * Presets documentation for reference:
 *
 * | Preset   | Width   | Quality | Format | Use Case             |
 * |----------|---------|---------|--------|----------------------|
 * | thumb    | 150px   | 95      | WebP   | List thumbnails      |
 * | preview  | 600px   | 95      | WebP   | Image grid           |
 * | medium   | 800px   | 95      | WebP   | Detail view          |
 * | large    | 1200px  | 95      | WebP   | Full-screen viewer   |
 * | original | —       | —       | —      | Publish operations   |
 */
